import { Button } from "@/components/ui/button";
import { api, type BooAccessEvent } from "@/lib/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldAlert } from "lucide-react";
import { describeAccessEvent, formatWhen, personName } from "./access-copy";

function noticeText(e: BooAccessEvent): string {
  const actor = personName(e.actor);
  const why = e.reason ? ` Reason: “${e.reason}”` : "";
  if (e.kind === "owner_assigned") {
    return `${actor}, who manages your organization, made ${personName(e.target)} an owner of this Boo on ${formatWhen(e.createdAt)}.${why}`;
  }
  if (e.kind === "ownership_taken") {
    return `${actor}, who manages your organization, took ownership of this Boo on ${formatWhen(e.createdAt)}.${why}`;
  }
  // Sharing decisions and handovers an org owner made for this Boo's people.
  return `${describeAccessEvent(e).replace(/\.$/, "")} on ${formatWhen(e.createdAt)}.${why}`;
}

// Banner for ownership changes an org owner made from outside the Boo. The
// backend leaves it out for the person who made the change and after two
// weeks; a Boo owner or admin can dismiss it sooner.
export function AccessNotices({ containerId, canDismiss }: { containerId: string; canDismiss: boolean }) {
  const queryClient = useQueryClient();
  const events = useQuery({
    queryKey: ["boo-access-events", containerId],
    queryFn: () => api.containers.members.events(containerId),
    retry: false,
  });
  const dismiss = useMutation({
    mutationFn: (eventId: string) => api.containers.members.dismissNotice(containerId, eventId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["boo-access-events", containerId] }),
  });
  const notices = (events.data?.data ?? []).filter((e) => e.notice);
  if (!notices.length) return null;

  return (
    <div className="mx-6 mt-2 flex flex-col gap-2">
      {notices.map((e) => (
        <div
          key={e.id}
          className="flex items-start gap-2 rounded-md border border-amber-300/50 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-950/30 dark:text-amber-200"
        >
          <ShieldAlert className="mt-0.5 size-4 shrink-0" />
          <span className="flex-1">{noticeText(e)}</span>
          {canDismiss && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              disabled={dismiss.isPending}
              onClick={() => dismiss.mutate(e.id)}
            >
              Dismiss
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}
